// NPM Imports
import React, { useContext, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// Component Imports
import HoverMRRDescription from './HoverMRRDescription'
import HoverDifferenceDescription from './HoverDifferenceDescription'
// JS Imports
import {
   //@ts-ignore
   formatFloatAsPercent,
   //@ts-ignore
   formatFloatAsCurrencySymbol,
} from '../../../common/helpers.mjs'
//@ts-ignore
import { getHighestProductBand } from '../../../common/calculate'

export default function CoreProductRow() {
   const state = useQuoteContext()
   const { getString, getFormattedString } = state
   const permissions = state.featurePermissions['NoneCoreOption']
   const permissionsShowMRROnDifferenceQuotes =
      state.featurePermissions['ShowMRROnDifferenceQuotes']
   const ShowMRROnDifferenceQuotes =
      permissionsShowMRROnDifferenceQuotes.includes(state.user_role)

   // Modal helper states
   const [show, setShow] = useState(false)
   const handleClose = () => setShow(false)
   const handleShow = () => setShow(true)
   const [prevValue, setPrevValue] = useState(state.productCoreEECount)

   // Determine visibility based on selected core product in Share popup
   const isExternalView = state.user_view === 'External'
   // Use state.coreProductSelected as source of truth — it is correctly loaded
   // from saved opp data on refresh, not the stale URL param.
   // If core0 selected: show only core0
   // If core1 selected: show core1, core2, core3
   // If core2 selected: show core2, core3
   // If core3 selected: show only core3
   const coreSelectedInternally = isExternalView
      ? state.coreProductSelected
      : null
   const shouldShowCore0 = !isExternalView || coreSelectedInternally === 'core0'
   const shouldShowCore1 = !isExternalView || coreSelectedInternally === 'core1'
   const shouldShowCore2 =
      !isExternalView ||
      coreSelectedInternally === 'core1' ||
      coreSelectedInternally === 'core2'
   const shouldShowCore3 =
      !isExternalView ||
      coreSelectedInternally === 'core1' ||
      coreSelectedInternally === 'core2' ||
      coreSelectedInternally === 'core3'

   // Checks if the selected core product is not in the pricebook and returns an empty div if true
   if (
      state.product_list['core1']?.NotInPricebook &&
      state.product_list['core2']?.NotInPricebook
   ) {
      return <> </>
   }

   return (
      <>
         {/* Warning Modal if above max pricing band for HRIS imp */}
         <Modal
            backdrop='static'
            keyboard={true}
            show={show}
            onHide={handleClose}
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString('product.CoreProductRow.Modal.Title')}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               {getFormattedString('product.CoreProductRow.Modal.Body', {
                  highestProductBand: getHighestProductBand(
                     state.product_list,
                     state.hrImplementationCosts.productCode,
                  )['EmpLow'],
               })}
            </Modal.Body>
            <Modal.Footer>
               <Button variant='primary' onClick={handleClose}>
                  {getString('product.CoreProductRow.Modal.Button')}
               </Button>
            </Modal.Footer>
         </Modal>
         {/* CoreProductMainRow */}
         <div className='row'>
            <div className='col-bhr1'>
               {(permissions.includes(state.user_role) ||
                  state.coreProductSelected === 'core0') &&
                  !state.product_list['core0']?.NotInPricebook &&
                  shouldShowCore0 && (
                     <div className='form-check form-check-inline'>
                        <input
                           className='form-check-input'
                           type='radio'
                           name='inlineRadioOptions'
                           id='core0Radio'
                           value={'core0'}
                           checked={state.coreProductSelected === 'core0'}
                           onChange={state.onCoreOptionChange}
                        ></input>
                        <label className='form-check-label'>
                           {state.product_list['core0']['ProductName']}
                        </label>
                     </div>
                  )}
               {/*Core1 visibility: Show if selected internally OR if in Internal view OR if core1/core0 selected in Share popup*/}
               {shouldShowCore1 &&
                  !state.product_list['core1']?.NotInPricebook && (
                     <div className='form-check form-check-inline'>
                        <input
                           className='form-check-input'
                           type='radio'
                           name='inlineRadioOptions'
                           id='core1Radio'
                           value='core1'
                           checked={state.coreProductSelected === 'core1'}
                           onChange={state.onCoreOptionChange}
                        ></input>
                        <label className='form-check-label'>
                           {state.product_list['core1']['ProductName']}
                        </label>
                     </div>
                  )}

               {/*Core2 visibility: Hide if core2 or core3 selected in Share popup*/}
               {!state.product_list['core2']?.NotInPricebook &&
                  shouldShowCore2 && (
                     <div className='form-check form-check-inline'>
                        <input
                           className='form-check-input'
                           type='radio'
                           name='inlineRadioOptions'
                           id='core2Radio'
                           value='core2'
                           checked={state.coreProductSelected === 'core2'}
                           onChange={state.onCoreOptionChange}
                        ></input>
                        <label className='form-check-label'>
                           {state.product_list['core2']['ProductName']}
                        </label>
                     </div>
                  )}
               {/* Core3 (Elite): same visibility rules as core1/core2 — pricebook + Share popup */}
               {!state.product_list['core3']?.NotInPricebook &&
                  shouldShowCore3 && (
                     <div className='form-check form-check-inline'>
                        <input
                           className='form-check-input'
                           type='radio'
                           name='inlineRadioOptions'
                           id='core3Radio'
                           value='core3'
                           checked={state.coreProductSelected === 'core3'}
                           onChange={state.onCoreOptionChange}
                        ></input>
                        <label className='form-check-label'>
                           {state.product_list['core3']['ProductName']}
                        </label>
                     </div>
                  )}
               <span>
                  (
                  {formatFloatAsCurrencySymbol(
                     state.product_list[state.coreProductSelected]?.[
                        'EmpListPrice'
                     ],
                     state.currencyObject,
                     true,
                  )}
                  )
               </span>
            </div>
            <div className='col-bhr2'>
               <input
                  className='form-control'
                  type='number'
                  placeholder={'0'}
                  value={
                     state.productCoreEECount <= 0
                        ? ''
                        : state.productCoreEECount.toString()
                  }
                  disabled={
                     state.coreProductSelected === 'core0' ? true : false
                  }
                  onBlur={(e) => {
                     state.onProductCoreInputBlur()
                     // Set "prevValue" state on clicking away from input
                     setPrevValue(Number(e.target.value))
                  }}
                  onChange={(e) => {
                     // If the core EE count moves above "x" (example, x=5000), when EE count was below "x" before, displays modal. If EE count already above "x" do not display modal again.
                     // Prevents modal from being displayed everytime EE count changes while greater than "x" and annoying users.
                     // Does not display modal for pricebook 90 or 100.
                     if (
                        state.pricebookId != 90 &&
                        state.pricebookId != 100 &&
                        e.target.value >=
                           getHighestProductBand(
                              state.product_list,
                              state.hrImplementationCosts.productCode,
                           )['EmpLow'] &&
                        prevValue <
                           getHighestProductBand(
                              state.product_list,
                              state.hrImplementationCosts.productCode,
                           )['EmpLow']
                     )
                        handleShow()

                     state.onProductCoreEECountChange(Number(e.target.value))
                  }}
               />
            </div>
            <div className='col col-bhr-red'>
               {formatFloatAsPercent(
                  state.productCoreMonthlyCosts.volumeDiscountedPEPMPercent,
               )}
            </div>
            <div className='col col-bhr-red'>
               {state.hasUSEmployees == true || state.user_view == 'Internal'
                  ? formatFloatAsPercent(
                       state.productCoreMonthlyCosts
                          .bundleDiscountedPEPMPercent,
                    )
                  : ''}
            </div>
            <div className='col'>
               {state.productCoreMonthlyCosts.discountedPEPMCost <=
               state.productCoreMonthlyCosts.listPEPMCost
                  ? formatFloatAsCurrencySymbol(
                       state.productCoreMonthlyCosts.discountedPEPMCost,
                       state.currencyObject,
                    )
                  : ''}
            </div>
            <div className='col'>
               {formatFloatAsCurrencySymbol(
                  state.productCoreMonthlyCosts.totalPrice,
                  state.currencyObject,
               )}
            </div>
            {state.differenceQuote == true ? (
               <>
                  {state.accountSnowflake.Elite_Rate_Plan__c &&
                  state.accountSnowflake.Billed_Elite__c ? (
                     <HoverDifferenceDescription
                        previousPlan={state.accountSnowflake.Elite_Rate_Plan__c}
                        previousQuantity={
                           state.accountSnowflake.Billed_Elite__c
                        }
                        previousInvoicedAmount={
                           state.productCoreMonthlyCosts
                              .previousTotalPriceUnconverted
                        }
                        previousStartDate={
                           state.accountSnowflake.Elite_Start_Date__c
                        }
                        previousCurrencyObject={state.currencyObjectPrevious}
                        placement='bottom'
                     >
                        <span className={'hoverable'}>
                           {formatFloatAsCurrencySymbol(
                              state.productCoreMonthlyCosts.previousTotalPrice,
                              state.currencyObject,
                           )}
                        </span>
                     </HoverDifferenceDescription>
                  ) : state.accountSnowflake.Pro_Rate_Plan__c &&
                    state.accountSnowflake.Billed_Pro__c ? (
                     <HoverDifferenceDescription
                        previousPlan={state.accountSnowflake.Pro_Rate_Plan__c}
                        previousQuantity={state.accountSnowflake.Billed_Pro__c}
                        previousInvoicedAmount={
                           state.productCoreMonthlyCosts
                              .previousTotalPriceUnconverted
                        }
                        previousStartDate={
                           state.accountSnowflake.Pro_Start_Date__c
                        }
                        previousCurrencyObject={state.currencyObjectPrevious}
                        placement='bottom'
                     >
                        <span className={'hoverable'}>
                           {formatFloatAsCurrencySymbol(
                              state.productCoreMonthlyCosts.previousTotalPrice,
                              state.currencyObject,
                           )}
                        </span>
                     </HoverDifferenceDescription>
                  ) : state.accountSnowflake.Core_Rate_Plan__c &&
                    state.accountSnowflake.Billed_Core__c ? (
                     <HoverDifferenceDescription
                        previousPlan={state.accountSnowflake.Core_Rate_Plan__c}
                        previousQuantity={state.accountSnowflake.Billed_Core__c}
                        previousInvoicedAmount={
                           state.productCoreMonthlyCosts
                              .previousTotalPriceUnconverted
                        }
                        previousStartDate={
                           state.accountSnowflake.Core_Start_Date__c
                        }
                        previousCurrencyObject={state.currencyObjectPrevious}
                        placement='bottom'
                     >
                        <span className={'hoverable'}>
                           {formatFloatAsCurrencySymbol(
                              state.productCoreMonthlyCosts.previousTotalPrice,
                              state.currencyObject,
                           )}
                        </span>
                     </HoverDifferenceDescription>
                  ) : (
                     <HoverDifferenceDescription
                        previousPlan=''
                        previousQuantity={0}
                        previousInvoicedAmount={0}
                        previousStartDate=''
                        previousCurrencyObject={state.currencyObject}
                        placement='bottom'
                     >
                        <span className={'col'}></span>
                     </HoverDifferenceDescription>
                  )}
                  {ShowMRROnDifferenceQuotes ? (
                     <HoverMRRDescription
                        listMRRTotal={
                           state.productCoreMonthlyCosts.listMRRTotal
                        }
                        previousMRRTotal={
                           state.productCoreMonthlyCosts.previousMRRTotal
                        }
                        MRRTotal={state.productCoreMonthlyCosts.MRRTotal}
                        MRRTotalUSD={state.productCoreMonthlyCosts.MRRTotalUSD}
                        currencyObjectCurrent={state.currencyObject}
                        currencyObjectUSD={state.currencyObjectUSD}
                     >
                        <span className='hoverable'>
                           {formatFloatAsCurrencySymbol(
                              state.productCoreMonthlyCosts
                                 .totalPriceDifference,
                              state.currencyObject,
                           )}
                        </span>
                     </HoverMRRDescription>
                  ) : (
                     <span className='col'>
                        {formatFloatAsCurrencySymbol(
                           state.productCoreMonthlyCosts.totalPriceDifference,
                           state.currencyObject,
                        )}
                     </span>
                  )}
               </>
            ) : (
               <></>
            )}
         </div>
      </>
   )
}
